<script>
  export let question;
  export let updateWorkingQuestion;
  
  function isPropsQuestion() {
    if (!question.dataset || !Array.isArray(question.dataset)) return false;
    return question.dataset.some((item) => 
      item[0] === "selfProps" || item[0] === "nodeProps"
    ) && !question.dataset.some((item) => item[0] === "setSelf");
  }
  
  function getEncryptionValue() {
    const encrypt = question.encrypt || [];
    
    // Convert array to single value for dropdown
    if (encrypt.length === 0 || (encrypt.length === 1 && encrypt[0] === 'none')) {
      return 'none';
    } else if (encrypt.length === 2 && encrypt.includes('project') && encrypt.includes('consent')) {
      return 'project-consent';
    } else if (encrypt.length === 1 && encrypt[0] === 'project') {
      return 'project';
    } else if (encrypt.length === 1 && encrypt[0] === 'consent') {
      return 'consent';
    }
    
    // Default to project-consent for multiple values
    return 'project-consent';
  }
  
  function updateEncryption(value) {
    let newEncrypt;
    
    switch (value) {
      case 'none':
        newEncrypt = ['none'];
        break;
      case 'project':
        newEncrypt = ['project'];
        break;
      case 'consent':
        newEncrypt = ['consent'];
        break;
      case 'project-consent':
        newEncrypt = ['project', 'consent'];
        break;
      default:
        newEncrypt = ['project', 'consent'];
    }
    
    updateWorkingQuestion('encrypt', newEncrypt);
  }
  
  // Reactive derived state
  $: isProps = isPropsQuestion();
  $: currentEncryption = getEncryptionValue();
  
  // Auto-fix encryption for props questions
  $: if (isProps && currentEncryption !== 'none') {
    updateEncryption('none');
  }
</script>

<div class="edit-section" class:auto-managed={isProps}>
  <h4>
    Encryption Settings
    {#if isProps}
      <span class="auto-badge">AUTO-MANAGED</span>
    {/if}
  </h4>
  
  <div class="form-styled">
    {#if isProps}
      <div class="auto-managed-info">
        <div class="auto-info-content">
          <span class="auto-icon">🔒</span>
          <div class="auto-text">
            <p class="auto-title">Props questions cannot have encryption</p>
            <p class="auto-description">Fixed at: <strong>none</strong></p>
          </div>
        </div>
      </div>
    {:else}
      <div class="field-wrapper">
        <label>Encryption Level</label>
        <select 
          value={currentEncryption}
          on:change={(e) => updateEncryption(e.target.value)}
          class="encryption-select"
        >
          <option value="none">none - No encryption</option>
          <option value="project">project - Project-level encryption only</option>
          <option value="consent">consent - Consent-based encryption only</option>
          <option value="project-consent">project, consent - Full encryption</option>
        </select>
        <p class="field-help">
          Choose encryption level for this question. Props questions are automatically set to "none".
        </p>
        
        <div class="encryption-info">
          <div class="info-grid">
            <div class="info-item">
              <strong>none:</strong> Data stored without encryption
            </div>
            <div class="info-item">
              <strong>project:</strong> Encrypted at project level
            </div>
            <div class="info-item">
              <strong>consent:</strong> Encrypted based on user consent
            </div>
            <div class="info-item">
              <strong>project, consent:</strong> Full encryption (recommended for sensitive data)
            </div>
          </div>
        </div>
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  .edit-section {
    border: 4px solid #d4d3bc;
    border-radius: 0;
    padding: 15px;
    margin-bottom: 20px;
    background: #f3f2e3;
    
    &.auto-managed {
      background: #f0f9ff;
      border-color: #0ea5e9;
    }
    
    h4 {
      margin-bottom: 15px;
      font-weight: bold;
      font-size: 0.9rem;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
  
  .auto-badge {
    background: #0ea5e9;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: bold;
    letter-spacing: 0.5px;
  }
  
  .auto-managed-info {
    padding: 15px;
    background: #e0f2fe;
    border: 2px solid #0ea5e9;
    border-radius: 6px;
    
    .auto-info-content {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .auto-icon {
        font-size: 1.5rem;
        flex-shrink: 0;
      }
      
      .auto-text {
        flex: 1;
        
        .auto-title {
          margin: 0 0 4px 0;
          font-size: 0.8rem;
          font-weight: 600;
          color: #0c4a6e;
        }
        
        .auto-description {
          margin: 0;
          font-size: 0.75rem;
          color: #075985;
          
          strong {
            background: #bae6fd;
            padding: 1px 4px;
            border-radius: 3px;
            font-family: monospace;
          }
        }
      }
    }
  }
  
  .field-wrapper {
    margin-bottom: 15px;
    
    label {
      display: inline-flex;
      font-weight: bold;
      margin-bottom: 5px;
      font-size: 0.75rem;
      color: #464b4b;
    }
  }
  
  .encryption-select {
    width: 100%;
    padding: 8px 12px;
    border: 4px solid #d4d3bc;
    border-radius: 0;
    background: #f3f2e3;
    font-size: 0.75rem;
    
    &:focus {
      outline: none;
      border-color: #10b981;
      background: #fff;
    }
  }
  
  .field-help {
    font-size: 11px;
    color: #6b7280;
    margin: 5px 0 15px 0;
    font-style: italic;
  }
  
  .encryption-info {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 12px;
    
    .info-grid {
      display: grid;
      gap: 8px;
      
      .info-item {
        font-size: 0.7rem;
        color: #475569;
        line-height: 1.3;
        
        strong {
          color: #1e293b;
          font-family: monospace;
          background: #e2e8f0;
          padding: 1px 4px;
          border-radius: 2px;
        }
      }
    }
  }
</style>